import React, {
	MouseEventHandler,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Notification } from '@/components/partials/notification/Notification';
import { Role } from '@/types/user';
import { timeline } from '@/utils/gsap';
import { useAuth } from '@/providers/AuthProvider';
import { useContent } from '@/providers/ContentProvider';
import { roleToAppPath, stripHTML } from '@/utils/utilities';
import { useScrollLock } from '@mantine/hooks';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Flex, UnstyledButton } from '@mantine/core';
import APP_CONFIG from '@/configs/appConfig';
import LogoNPB from '@/assets/images/logo.svg';
import SignWithApple from '@/assets/images/appleid_button@2x.png';
import Icons from '@/assets/Icons';
import styles from './LandingPage.module.scss';

const START_STYLES = {
	'clip-path': 'polygon(0 100%, 100% 100%, 100% 100%, 0% 100%)',
	opacity: 0,
	y: 50,
};

const baseSlug = `panelist_landing_`;

export const LandingPage: React.FC = () => {
	const anchorRef = useRef<HTMLAnchorElement>(null!);

	const { getText, getContent } = useContent();

	const [searchParams, setSearchParams] = useSearchParams();

	const [errorMessage, setErrorMessage] = useState('');

	const { handleCompletedLogin } = useAuth();

	const [, setScrollLocked] = useScrollLock(true);

	const isIos = Capacitor.getPlatform() === 'ios';

	useLayoutEffect(() => {
		if (!searchParams.toString()) {
			const heroImg = document.getElementById('landing-hero');
			if (heroImg) heroImg.style.opacity = '0';
			timeline()
				.from(`h1, .${styles.logo}`, {
					...START_STYLES,
					duration: 2.2,
				})
				.from(
					`.${styles.option}`,
					{
						...START_STYLES,
						stagger: 0.2,
					},
					'-=2'
				)
				.to('figure', { opacity: 1 }, '-=1.5')
				.from(
					`.${styles.register}`,
					{
						...START_STYLES,
						stagger: 0.2,
					},
					'-=1.8'
				);

			setTimeout(() => {
				setScrollLocked(false);
			}, 3000);
		}

		return () => setScrollLocked(false);
	}, []);

	useEffect(() => {
		const accessToken = searchParams.get('accessToken');
		const refreshToken = searchParams.get('refreshToken');
		const message = searchParams.get('message');

		if (message) {
			setErrorMessage(stripHTML(message));
			return;
		}

		if (!accessToken || !refreshToken) return;

		setSearchParams({});

		const success = handleCompletedLogin(accessToken, refreshToken);

		if (!success)
			setErrorMessage(
				'Otrzymany token jest niepoprawny! Spróbuj ponownie później'
			);
	}, [searchParams]);

	const handleSocialsClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
		if (!Capacitor.isNativePlatform()) return;

		e.preventDefault();

		isIos
			? window.open(e.currentTarget.href, '_blank')
			: Browser.open({
				url: e.currentTarget.href,
				toolbarColor: APP_CONFIG.CAPACITOR_BROWSER_TOOLBAR_COLOR,
			});
	};

	const handleSignWithAppleClick: MouseEventHandler<HTMLButtonElement> = (
		e
	) => {
		isIos
			? window.open(
				`${import.meta.env.VITE_API_BASE_URL}social/apple/login`,
				'_blank'
			)
			: Browser.open({
				url: `${import.meta.env.VITE_API_BASE_URL}social/apple/login`,
				toolbarColor: APP_CONFIG.CAPACITOR_BROWSER_TOOLBAR_COLOR,
			});
	};

	return (
		<section className={styles.container}>
			<a href="" ref={anchorRef} className="hidden-element"></a>
			<img className={styles.logo} src={LogoNPB} alt="NPB logo" />
			<h1>{getText(`${baseSlug}header`)}</h1>
			{errorMessage && (
				<Notification content={<p>{errorMessage}</p>} theme="error" />
			)}
			<div className={styles.loginOptions}>
				<a
					href={`${import.meta.env.VITE_API_BASE_URL}social/facebook/login`}
					onClick={handleSocialsClick}
				>
					<div className={styles.option}>
						<div className={styles.circle}>{Icons.facebook} </div>
						<p>{getText(`${baseSlug}loginFacebookLabel`)}</p>
					</div>
				</a>
				<a
					href={`${import.meta.env.VITE_API_BASE_URL}social/google/login`}
					onClick={handleSocialsClick}
				>
					<div className={styles.option}>
						<div className={styles.circle}>{Icons.gmail}</div>
						<p>{getText(`${baseSlug}loginGmailLabel`)}</p>
					</div>
				</a>
				<a
					href={`${import.meta.env.VITE_API_BASE_URL}social/linkedin/login`}
					onClick={handleSocialsClick}
				>
					<div className={styles.option}>
						<div className={styles.circle}>{Icons.linkedIn} </div>
						<p>{getText(`${baseSlug}loginLinkedInLabel`)}</p>
					</div>
				</a>
				<Link to="login">
					<div className={styles.option}>
						<div className={styles.circle}>{Icons.mail}</div>
						<p>{getText(`${baseSlug}loginEmailLabel`)}</p>
					</div>
				</Link>
			</div>
			{isIos && (
				<Flex justify="center" px="sm">
					<UnstyledButton
						maw={400}
						mb="md"
						display="block"
						lh={0}
						onClick={handleSignWithAppleClick}
						className={styles.register}
					>
						<img src={SignWithApple} />
					</UnstyledButton>
				</Flex>
			)}
			<div className={styles.register}>
				{getContent('panelist_landing_information', {
					$register: `/${roleToAppPath(Role.PANELIST)}/register`,
				})}
			</div>
		</section>
	);
};
